import {createApp} from 'vue';
import Toast from '@/components/Toast';
import Progress from '@/components/Progress';
import Dialog from '@/components/Dialog';
import ConfirmContent from '@/components/ConfirmContent';
import NotifyContent from '@/components/NotifyContent';
import ImageContent from '@/components/ImageContent';
import CalloutContent from '@/components/CalloutContent.vue';

export default function uiServiceFactory(services) {
  let thisService = {
    init,
    toast,
    showProgress,
    hideProgress,
    updateProgress,
    scrollIntoView,
    addInlineAlert,
    dismissInlineAlert,
    removeInlineAlert,
    resetInlineAlertAsync,
    resetTour,
    resetTutorial,
    showDialog,
    showConfirm,
    showNotify,
    showImage,
    showCallout,
    startTour,
    startTutorial,
  };

  let progressApp;
  let progressMount;

  let dialogs = [];
  let dialogIndex = 0;
  let calloutTargetElement = null;
  let calloutElement = null;
  let calloutPositionElement = null;
  let calloutConfig = null;
  let targetObserver = new MutationObserver(handleMutation);
  let observerIntervalId = null;
  return thisService;

  ////////////////////

  function init() {
    restartTutorial();
  }

  function toast(config) {
    // config = {
    //   title: 'My Title',
    //   // description: 'My Description',
    //   action: {
    //     title: 'Action Title',
    //     callback: () => {
    //       alert('callback clicked');
    //     }
    //   },
    //   icon: 'apple',
    //   position: 'lowerRight'
    // };
    if (!config) {
      return;
    }

    let mountPoint = document.createElement('div');
    document.body.appendChild(mountPoint);

    const props = {
      config
    };

    let listener = () => {
      setTimeout(() => {
        toast.unmount();
        document.body.removeChild(mountPoint);
      }, 1000);
      services.window.removeEventListener('pos-toast-closed', listener)
    };

    let toast = createApp(Toast, props);
    services.window.addEventListener('pos-toast-closed', listener);
    toast.mount(mountPoint);
  }

  async function showDialog(config = {}) {
    let id = config.id ? config.id : `dialog${dialogIndex++}`;
    if (dialogs.includes(id)) {
      return;
    }

    let mount;
    let app;
    return new Promise((resolve, reject) => {
      // Make sure we have an ID for the dialog
      config.id = id;
      config.error = null;

      // Make sure we have an API container set up for dialog and content APIs
      if (!config.api) {
        config.api = {};
      }
      config.api.dialog = {};

      // Dialog content should set this up to return data to caller
      config.api.setCallback = (id, callback) => {
        switch (id) {
          case 'handleData':
            config.api.getData = callback;
            break;
          case 'handleDataAsync':
            config.api.getDataAsync = callback;
            break;
          case 'handleSubmit':
            config.api.handleSubmit = callback;
            break;
          case 'handleCancel':
            config.api.handleCancel = callback;
            break;
          default:
            console.warn('Invalid callback (handleData, handleDataAsync, handleSubmit, handleCancel)', id);
        }
      };

      config.api.resolve = data => {
        if (config.api.getData) {
          resolve(config.api.getData())
          config.api.close();
          return Promise.resolve();
        }

        if (config.api.getDataAsync) {
          return config.api.getDataAsync().then(data => {
            resolve(data);
            config.api.close();
            return Promise.resolve();
          });
        }

        resolve(data);
        config.api.close();
        return Promise.resolve();
      };

      config.api.reject = data => {
        if (config.api.getData) {
          reject(config.api.getData())
          config.api.close();
          return Promise.resolve();
        }

        if (config.api.getDataAsync) {
          return config.api.getDataAsync().then(data => {
            reject(data);
            config.api.close();
            return Promise.resolve();
          });
        }

        reject(data);
        config.api.close();
        return Promise.resolve();
      };

      config.api.cancel = () => {
        if (config.api.handleCancel) {
          return config.api.handleCancel().catch(() => {});
        }
        else {
          return config.api.reject();
        }
      }

      config.api.submit = () => {
        if (config.api.handleSubmit) {
          return config.api.handleSubmit().catch(() => {});
        }
        else {
          return config.api.resolve();
        }
      }

      // Dialog content can override buttons if the want anything other than
      // the standard cancel and submit
      if (!config.buttons) {
        config.buttons = [];
        if (!config.hideCancel) {
          config.buttons.push({
            id: 'cancel',
            type: 'cancel',
            label: config.cancelLabel ? config.cancelLabel : services.i18n.$t('uxf.Dialog_Cancel'),
            disabled: false,
            handleClick: myConfig => {
              return myConfig.api.cancel(myConfig);
            }
          });
        }

        if (!config.hideSubmit) {
          config.buttons.push({
            id: 'submit',
            type: 'submit',
            label: config.submitLabel ? config.submitLabel : services.i18n.$t('uxf.Dialog_Submit'),
            disabled: false,
            handleClick: myConfig => {
              return myConfig.api.submit(myConfig);
            }
          });
        }
      }

      config.api.setButtons = buttons => {
        let myButtons = buttons ? buttons : [];
        myButtons.forEach(b => {
          if (b.id === 'cancel') {
            b.type = 'cancel';
            b.label = b.label ? b.label : services.i18n.$t('uxf.Dialog_Cancel');
            b.handleClick = myConfig => {
              return myConfig.api.cancel(myConfig);
            };
          }
          if (b.id === 'submit') {
            b.type = 'submit';
            b.label = b.label ? b.label : services.i18n.$t('uxf.Dialog_Submit');
            b.handleClick = myConfig => {
              return myConfig.api.submit(myConfig);
            };
          }
          if (b.disabled === undefined || b.disabled === null) {
            b.disabled = false;
          }
        });

        config.buttons = myButtons;
        setTimeout(() => {  services.session.sendClientEvent('pros-dialog-button-update')});
      }

      config.api.setDisabled = (id, disabled) => {
        let found = config.buttons.find(b => { return b.id === id });
        if (!found) { return }
        found.disabled = disabled;
        services.session.sendClientEvent('pros-dialog-button-update');
      };

      config.api.disableSubmit = disable => {
        let submit = config.buttons.find(b => { return b.type === 'submit' });
        if (submit) {
          submit.disabled = disable;
        }
        services.session.sendClientEvent('pros-dialog-button-update');
      }

      // (Optional) Dialog content can set up their own API that caller can call
      config.api.setDialogAPI = api => {
        config.api.dialog = api;
      };


      // Set dialog title
      config.api.setTitle = title => {
        config.title = title;
        services.session.sendClientEvent('pros-dialog-title-update');
      };

      // Close dialog
      config.api.close = () => {
        if (!mount) {
          return;
        }

        app.unmount();
        app = null;
        document.body.removeChild(mount);
        mount = null;
        dialogs.splice(dialogs.indexOf(config.id), 1);
      };

      // Allow error to be set/cleared
      config.api.setError = (errorMessage, severity) => {
        let mySeverity = severity ? severity : 'error';
        console.log('Dialog - setError', errorMessage, mySeverity);
        if (errorMessage) {
          config.error = {
            message: errorMessage,
            severity: mySeverity,
          }
        }
        else {
          config.error = null;
        }

        services.session.sendClientEvent('pros-dialog-error-update', {config});
      };

      config.api.clearError = () => {
        config.error = null;
        services.session.sendClientEvent('pros-dialog-error-update', {config});
      };

      services.session.sendClientEvent('pros-dialog-api-ready', {config});

      mount = document.createElement('div');

      mount.style.position = 'absolute';
      mount.style.top = '0';
      mount.style.right = '0';
      mount.style.bottom = '0';
      mount.style.left = '0';
      mount.style.zIndex = '1000';
      document.body.appendChild(mount);

      app = createApp(Dialog, {config});
      app.mount(mount);
      dialogs.push(config.id);
    });

  }

  function showConfirm(config = {}) {
      config.title = config.title ? config.title : services.i18n.$t('header.ui.Confirm');
      config.buttons = [
        {
          id: 'cancel',
          label: config.cancelLabel ? config.cancelLabel : services.i18n.$t('uxf.Dialog_Cancel'),
          display: 'secondary',
          disabled: false,
          handleClick: dialogConfig => {
            return dialogConfig.api.cancel(dialogConfig);
          }
        },
        {
          id: 'confirm',
          label: config.confirmLabel ? config.confirmLabel : services.i18n.$t('uxf.Dialog_Confirm'),
          disabled: false,
          handleClick: dialogConfig => {
            return dialogConfig.api.submit(dialogConfig);
          }
        },
      ];

      config.getContent = myConfig => {
        return mountVueComponent(ConfirmContent,  {config: myConfig});
      }
      return showDialog(config).then(resp => {
        return Promise.resolve(resp ? resp : true);
      })
        .catch(() => {
          return Promise.reject(false);
        });
  }

  function showImage(config = {}) {
    config.title = config.title ? config.title : services.i18n.$t('header.ui.SelectImage');
    config.getContent = myConfig => {
      return mountVueComponent(ImageContent,  {config: myConfig});
    }
    return showDialog(config);
  }

  async function showCallout(config = {}) {
    removeCallout();
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        calloutConfig = {...config};
        calloutConfig.title = calloutConfig.title ? calloutConfig.title : 'Title';
        calloutConfig.message = calloutConfig.message ? calloutConfig.message : 'Message goes here';
        calloutConfig.steps = calloutConfig.steps !== null && calloutConfig.steps !== undefined ? Number(config.steps) : 0;
        calloutConfig.step = calloutConfig.step !== null && calloutConfig.step !== undefined ? Number(config.step) : 0;
        calloutConfig.placement = calloutConfig.placement ? calloutConfig.placement : 'bottom';
        calloutConfig.arrowPlacement = calloutConfig.arrowPlacement ? calloutConfig.arrowPlacement : 'center';
        calloutElement = mountVueComponent(CalloutContent,  {config: calloutConfig});
        document.body.appendChild(calloutElement);
        let calloutRect = document.body.getElementsByClassName('posb-callout-content')[0].getBoundingClientRect();
        calloutElement.remove();
        let searchTime = 0;
        let intervalId = setInterval(() => {
          calloutTargetElement = document.getElementById(calloutConfig.targetElementId);
          if (calloutTargetElement && calloutTargetElement.offsetParent !== null) {
            clearInterval(intervalId);
            window.addEventListener('scroll', handleCalloutPosition, true);
            targetObserver.observe(calloutTargetElement, {attributes: true, childList: true, subtree: true});
            observerIntervalId = setInterval(() => {
              if (calloutTargetElement.offsetParent === null) {
                calloutConfig.close();
              }
            }, 200);

            calloutTargetElement.scrollIntoView({behavior: 'smooth', black: 'start', inline: 'nearest'});

            if (calloutConfig.type === 'click') {
              calloutTargetElement.addEventListener('click', handleTargetClicked);
            }

            calloutPositionElement = document.createElement('div');
            calloutConfig.prev = () => {
              if (calloutConfig.callbacks?.handlePrev) {
                calloutConfig.callbacks.handlePrev();
              }
              removeCallout();
              resolve();
            }

            calloutConfig.next = () => {
              if (calloutConfig.callbacks?.handleNext) {
                calloutConfig.callbacks.handleNext();
              }
              removeCallout();
              resolve();
            }

            calloutConfig.close = () => {
              // remove and destroy calloutElement
              if (calloutConfig.callbacks?.handleClose) {
                calloutConfig.callbacks.handleClose();
              }
              removeCallout();
              resolve();
            }

            calloutPositionElement.style.position = 'absolute';
            calloutPositionElement.style.zIndex = 1000;
            calloutPositionElement.appendChild(calloutElement);
            document.body.appendChild(calloutPositionElement);

            handleCalloutPosition();
          }
          else {
            searchTime += 100;
            if (searchTime >= 30 * 1000) {
              clearInterval(intervalId);
              console.error('Target element not found within 30 seconds');
              reject();
            }
          }
        }, 100);
      });
    });
  }

  function handleMutation() {
    handleCalloutPosition();
  }

  function removeCallout() {
    if (calloutPositionElement) {
      calloutPositionElement.remove();
    }
    calloutPositionElement = null;
    calloutTargetElement = null;
    calloutElement = null;
    calloutConfig = null;
    if (targetObserver) {
      targetObserver.disconnect();
    }
    if (observerIntervalId) {
      clearInterval(observerIntervalId);
    }
    window.removeEventListener('scroll', handleCalloutPosition, true);
  }

  function handleTargetClicked(e) {
    calloutTargetElement.removeEventListener('click', handleTargetClicked);
    if (calloutConfig.parent === 'tour' || calloutConfig.parent === 'tutorial') {
      calloutConfig.next();
    }
    else {
      calloutConfig.close();
    }
  }

  function handleCalloutPosition() {
    if (calloutTargetElement && calloutTargetElement.offsetParent !== null) {
      let targetRect = calloutTargetElement.getBoundingClientRect();
      let calloutRect = calloutElement.children[0].getBoundingClientRect();
      let placement = getCalloutPlacement(calloutConfig.placement, calloutConfig.arrowPlacement, targetRect, calloutRect);
      if (placement) {
        calloutPositionElement.style.top = `${placement.position.top}px`;
        calloutPositionElement.style.left = `${placement.position.left}px`;
        services.session.sendClientEvent('pros-callout-update-placement', placement);
      }
    }
    else {
      if (calloutConfig) {
        calloutConfig.close();
      }
    }
  }

  function resetTour(tourId) {
    let tours = services.preferences.getPreference('tours', 'posh') || [];
    tours = tours.filter(t => t !== tourId);
    services.preferences.setPreferenceAsync('tours', tours, 'posh');
  }

  async function startTour(config = {}) {
    if (config.runOnce) {
      if (!config.id) {
        console.error('Tutorial must have an ID to run once');
        return;
      }

      let tours = services.preferences.getPreference('tours', 'posh') || [];
      if (tours.includes(config.id)) {
        return;
      }
      else {
        tours.push(config.id);
        services.preferences.setPreferenceAsync('tours', tours, 'posh');
      }
    }

    let step = 0;
    while (step < config.steps.length) {
      let stepConfig = {...config.steps[step]};
      stepConfig.steps = config.steps.length;
      stepConfig.step = step;
      stepConfig.parent = 'tour';
      stepConfig.callbacks = {
        handleClose: (() => {
          step = config.steps.length;
        }),
        handleNext: (() => {
          step++;
        }),
        handlePrev: (() => {
          step--;
        })
      };
      await showCallout(stepConfig).catch(() => {
        stepConfig.steps = config.steps.length;
      });
    }
  }

  function resetTutorial(tutorialId) {
    let tutorials = services.preferences.getPreference('tutorials', 'posh') || [];
    tutorials = tutorials.filter(t => t !== tutorialId);
    services.preferences.setPreferenceAsync('tutorials', tutorials, 'posh');
  }

  function startTutorial(config = {}) {
    if (config.runOnce) {
      if (!config.id) {
        console.error('Tutorial must have an ID to run once');
        return;
      }

      let tutorials = services.preferences.getPreference('tutorials', 'posh') || [];
      if (tutorials.includes(config.id)) {
        return;
      }
    }

    // adjust tutorial steps
    let mySteps = [];
    let stepIndex = 0;
    let currentAppId = services.app.getCurrentAppId();
    let currentPageId = services.app.getPageId();
    config.steps.forEach(step => {
      switch (step.type) {
        case 'switch-application':
          if (currentAppId !== step.appId) {
            mySteps.push({
              type: 'click',
              targetElementId: 'app-switcher-toggle',
              appId: step.appId,
              title: 'Open Application Switcher',
              message: 'The application switcher is where you go to switch between applications',
              placement: 'right',
              arrowPlacement: 'start',
              step: stepIndex++,
            });
            mySteps.push({
              type: 'click',
              targetElementId: `posb-app-${step.appId}`,
              title: 'Select Application',
              message: 'Click on the application to switch to it',
              pageId: step.pageId,
              placement: 'right',
              arrowPlacement: 'center',
              step: stepIndex++,
            });
            currentAppId = step.appId;
            currentPageId = null;
          }
          break;
        case 'switch-view':
          if (currentPageId !== step.pageId) {
            mySteps.push({
              type: 'click',
              targetElementId: `view-${step.viewId}`,
              title: 'Select Page',
              message: 'Click on the item to select the page',
              placement: 'right',
              arrowPlacement: 'center',
              step: stepIndex++,
            });
            currentPageId = step.viewId;
          }
          break;
        case 'click':
          mySteps.push({
            ...step,
            step: stepIndex++,
          });
          break;
        case 'timer':
          mySteps.push({
            ...step,
            step: stepIndex++,
          });
          break;
        case 'manual':
        default:
          mySteps.push({
            ...step,
            step: stepIndex++,
          });
          break;
      }
    });
    // store tutorial in local storage
    let tutorial = {
      id: config.id,
      steps: mySteps,
      step: 0,
    };
    services.preferences.setLocalStorageObject('tutorial', tutorial);
    runTutorialSteps(tutorial);
  }

  function restartTutorial() {
    let tutorial = services.preferences.getLocalStorageObject('tutorial');
    if (tutorial) {
      runTutorialSteps(tutorial);
    }
  }

  async function runTutorialSteps(tutorial) {
    while (tutorial.step < tutorial.steps.length) {
      let stepConfig = {...tutorial.steps[tutorial.step]};
      stepConfig.parent = 'tutorial';
      stepConfig.steps = tutorial.steps.length;
      stepConfig.step = tutorial.step;
      stepConfig.callbacks = {
        handleClose: (() => {
          tutorial.step = tutorial.steps.length;
        }),
        handleNext: (() => {
          tutorial.step++;
          services.preferences.setLocalStorageObject('tutorial', tutorial);
        }),
        handlePrev: (() => {
          tutorial.step--;
          services.preferences.setLocalStorageObject('tutorial', tutorial);
        })
      };
      await showCallout(stepConfig).catch(() => {
        tutorial.step = tutorial.steps.length;
        services.preferences.deleteLocalStorage('tutorial');
      });
    }

    if (tutorial.id){
      let tutorials = services.preferences.getPreference('tutorials', 'posh') || [];
      tutorials.push(tutorial.id);
      services.preferences.setPreferenceAsync('tutorials', tutorials, 'posh');
    }

    services.preferences.deleteLocalStorage('tutorial');
  }

  function getCalloutPlacement(placement, arrowPlacement, targetRect, displayRect) {
    let position = null;
    let myPlacement = placement;
    let myArrowPlacement = arrowPlacement;
    let PLACEMENT = ['top','right','bottom','left'];
    while (PLACEMENT.includes(placement) && PLACEMENT[0] !== placement) {
      let move = PLACEMENT.shift();
      PLACEMENT.push(move);
    }

    let ARROW_PLACEMENT = ['start','center','end'];
    while (ARROW_PLACEMENT.includes(arrowPlacement) && ARROW_PLACEMENT[0] !== arrowPlacement) {
      let move = ARROW_PLACEMENT.shift();
      ARROW_PLACEMENT.push(move);
    }

    for (let i = 0; i < PLACEMENT.length; i++) {
      for (let j = 0; j < ARROW_PLACEMENT.length; j++) {
        myPlacement = PLACEMENT[i];
        myArrowPlacement = ARROW_PLACEMENT[j];
        position = getValidPosition(myPlacement, myArrowPlacement, targetRect, displayRect);
        if (position) {
          break;
        }
      }
      if (position) {
        break;
      }
    }

    return position ? {
      placement: myPlacement,
      arrowPlacement: myArrowPlacement,
      position
    } : null;
  }

  function getValidPosition(placement, arrowPlacement, targetRect, displayRect) {
    let position = null;
    let clientW = document.body.clientWidth;
    let clientH = document.body.clientHeight;
    let spaceAbove = clientH - targetRect.top;
    let spaceRight = clientW - targetRect.right;
    let spaceBelow = clientH - targetRect.bottom;
    let spaceLeft = targetRect.left;
    let centerW = targetRect.left + (targetRect.width / 2);
    let centerH = targetRect.top + (targetRect.height / 2);
    let contentW = placement === 'left' || placement === 'right' ? displayRect.width + 8 : displayRect.width;
    let contentH = placement === 'top' || placement === 'bottom' ? displayRect.height + 8 : displayRect.height;
    let hOffset = targetRect.width < 32 ? 32 - targetRect.width : 0;
    let vOffset = targetRect.height < 32 ? 32 - targetRect.height : 0;

    switch (placement) {
      case 'top':
        if (spaceAbove >= contentH) {
          switch (arrowPlacement) {
            case 'start':
              if (spaceLeft - hOffset > 0) {
                position = {
                  top: targetRect.top - contentH,
                  left: targetRect.left - hOffset,
                }
              }
              break;
            case 'center':
              if ( centerW - (contentW / 2) >= 0 && centerW + (contentW / 2) <= clientW ) {
                position = {
                  top: targetRect.top - contentH,
                  left: centerW - (contentW / 2),
                }
              }
              break;
            case 'end':
              if (targetRect.right - contentW + hOffset > 0) {
                position = {
                  top: targetRect.top - contentH,
                  left: targetRect.right - contentW + hOffset,
                }
              }
              break;
          }
        }
        break;
      case 'right':
        if (spaceRight >= contentW) {
          switch (arrowPlacement) {
            case 'start':
              if (spaceAbove - vOffset > 0) {
                position = {
                  top: targetRect.top - vOffset,
                  left: targetRect.right,
                }
              }
              break;
            case 'center':
              if (centerH - (contentH / 2) >= 0 ) {
                position = {
                  top: centerH - (contentH / 2),
                  left: targetRect.right,
                }
              }
              break;
            case 'end':
              if (spaceBelow - vOffset > 0) {
                position = {
                  top: targetRect.bottom - contentH + vOffset,
                  left: targetRect.right,
                }
              }
              break;
          }
        }
        break;
      case 'left':
        if (spaceLeft >= contentW) {
          switch (arrowPlacement) {
            case 'start':
              if (spaceAbove - vOffset > 0) {
                position = {
                  top: targetRect.top - vOffset,
                  left: targetRect.left - contentW,
                }
              }
              break;
            case 'center':
              if (centerH - (contentH / 2) >= 0) {
                position = {
                  top: centerH - (contentH / 2),
                  left: targetRect.left - contentW,
                }
              }
              break;
            case 'end':
              if (spaceBelow - vOffset > 0) {
                position = {
                  top: targetRect.bottom - contentH + vOffset,
                  left: targetRect.left - contentW,
                }
              }
              break;
          }
        }
        break;
      case 'bottom':
      default:
        if (spaceBelow > contentH) {
          switch (arrowPlacement) {
            case 'start':
              if (spaceLeft - hOffset > 0) {
                position = {
                  top: targetRect.bottom,
                  left: targetRect.left - hOffset,
                }
              }
              break;
            case 'center':
              if ( centerW - (contentW / 2) >= 0 && centerW + (contentW / 2) <= clientW ) {
                position = {
                  top: targetRect.bottom,
                  left: centerW - (contentW / 2),
                }
              }
              break;
            case 'end':
              if (targetRect.right - contentW + hOffset > 0) {
                position = {
                  top: targetRect.bottom,
                  left: targetRect.right - contentW + hOffset,
                }
              }
              break;
          }
        }
        break;
    }

    return position;
  }

  function showNotify(config = {}) {
    return new Promise((resolve, reject) => {
      config.title = config.title ? config.title : services.i18n.$t('header.ui.Notify');
      config.buttons = [
        {
          id: 'done',
          label: config.doneLabel ? config.doneLabel : services.i18n.$t('uxf.Dialog_Done'),
          handleClick: dialogConfig => {
            dialogConfig.api.close();
            return Promise.resolve(resolve(true));
          }
        },
      ];

      config.getContent = myConfig => {
        return mountVueComponent(NotifyContent,  {config: myConfig});
      }
      return showDialog(config).catch(() => {
        return Promise.resolve();
      });
    });
  }

  function showProgress(config = {}) {
    if (progressApp) {
      return;
    }

    progressMount = document.createElement('div');
    progressMount.style.position = 'absolute';
    if (config.allowClicks) {
      progressMount.style.top = '50%';
      progressMount.style.left = '50%';
      progressMount.style.transform = 'translate(-50%, -50%)';
    }
    else {
      progressMount.style.top = '0';
      progressMount.style.right = '0';
      progressMount.style.bottom = '0';
      progressMount.style.left = '0';
    }
    progressMount.style.zIndex = '1020';
    document.body.appendChild(progressMount);

    progressApp = createApp(Progress, {config});
    progressApp.mount(progressMount);
  }

  function hideProgress() {
    if (!progressMount) {
      return;
    }

    progressApp.unmount();
    progressApp = null;
    document.body.removeChild(progressMount);
    progressMount = null;
  }

  function updateProgress(options) {
    services.session.sendClientEvent('posh-progress-update', options);
  }

  function scrollIntoView(elementId) {
    let element = document.getElementById(elementId);
    if (!element) {
      console.warn(`Element with ID ${elementId} not found`);
      return;
    }

    document.getElementById(elementId).scrollIntoView({behavior: 'smooth'});
  }

  function addInlineAlert(inlineAlert) {
    services.session.sendClientEvent('posh-add-inline-event', inlineAlert)
  }

  function dismissInlineAlert(inlineAlertId) {
    services.session.sendClientEvent('posh-dismiss-inline-event', inlineAlertId)
  }

  function removeInlineAlert(inlineAlertId) {
    services.session.sendClientEvent('posh-remove-inline-event', inlineAlertId)
  }

  function resetInlineAlertAsync(inlineAlertId) {
    services.session.sendClientEvent('posh-remove-inline-event', inlineAlertId)
    return services.preferences.deletePreferenceAsync(inlineAlertId, 'inlineAlerts');
  }

  function mountVueComponent(content, propsData = {}) {
    const tempDiv = document.createElement('div');
    let instance = createApp(content, propsData);
    instance.use(services.i18n.getI18nInstance());
    instance.mount(tempDiv);
    return tempDiv;
  }
}
